import { z } from "zod";
import { translateApp } from "@/i18n";
import { DEFAULT_ITINERARY_PDF_THEME } from "@/pdf/styles";
export const defaultOrganizationBrandTheme = {
    fontFamily: "Helvetica",
    colors: {
        primary: "#f3764a",
        text: "#262626",
        accent: "#00c4cb",
        success: "#33b436",
        background: "#ffffff",
    },
    support: {
        name: null,
        email: null,
    },
    assets: {
        publicLogoUrl: null,
        logoStorageId: null,
        logoUrl: null,
    },
};
export const organizationBrandThemeColorFields = [
    { key: "primary", labelKey: "settings.brand_theme.colors.primary" },
    { key: "text", labelKey: "settings.brand_theme.colors.text" },
    { key: "accent", labelKey: "settings.brand_theme.colors.accent" },
    { key: "success", labelKey: "settings.brand_theme.colors.success" },
    { key: "background", labelKey: "settings.brand_theme.colors.background" },
];
const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9A-Fa-f]{6}$/, translateApp("settings.brand_theme.validation.hex_color"));
export const organizationBrandThemeColorsSchema = z.object({
    primary: hexColorSchema,
    text: hexColorSchema,
    accent: hexColorSchema,
    success: hexColorSchema,
    background: hexColorSchema,
});
export const organizationBrandThemeSchema = z.object({
    fontFamily: z.string().trim().min(1).max(255),
    colors: organizationBrandThemeColorsSchema,
    support: z.object({
        name: z.string().trim().max(120).nullable(),
        email: z.string().trim().email().max(254).nullable(),
    }),
    assets: z.object({
        publicLogoUrl: z
            .string()
            .trim()
            .url()
            .refine((value) => value.startsWith("https://"), {
            message: translateApp("settings.brand_theme.validation.https_url"),
        })
            .nullable()
            .optional(),
        logoStorageId: z.number().int().positive().nullable(),
        logoUrl: z.string().trim().url().nullable().optional(),
    }),
});
export const updateOrganizationBrandThemeSchema = z.object({
    fontFamily: z.string().trim().min(1).max(255).optional(),
    colors: organizationBrandThemeColorsSchema.partial().optional(),
    support: organizationBrandThemeSchema.shape.support.partial().optional(),
    assets: organizationBrandThemeSchema.shape.assets.partial().optional(),
});
export function normalizeOrganizationBrandTheme(value) {
    const colors = value?.colors ?? {};
    const assets = value?.assets;
    const fontFamily = typeof value?.fontFamily === "string" && value.fontFamily.trim().length > 0
        ? value.fontFamily.trim()
        : defaultOrganizationBrandTheme.fontFamily;
    return {
        fontFamily,
        colors: {
            primary: isHexColor(colors.primary)
                ? colors.primary
                : defaultOrganizationBrandTheme.colors.primary,
            text: isHexColor(colors.text)
                ? colors.text
                : defaultOrganizationBrandTheme.colors.text,
            accent: isHexColor(colors.accent)
                ? colors.accent
                : defaultOrganizationBrandTheme.colors.accent,
            success: isHexColor(colors.success)
                ? colors.success
                : defaultOrganizationBrandTheme.colors.success,
            background: isHexColor(colors.background)
                ? colors.background
                : defaultOrganizationBrandTheme.colors.background,
        },
        support: {
            name: normalizeNullableString(value?.support?.name),
            email: normalizeEmail(value?.support?.email),
        },
        assets: {
            publicLogoUrl: normalizeHttpsUrl(assets?.publicLogoUrl),
            logoStorageId: normalizePositiveInteger(assets?.logoStorageId),
            logoUrl: normalizeHttpsUrl(assets?.logoUrl),
        },
    };
}
export function toItineraryPdfTheme(theme) {
    const normalized = normalizeOrganizationBrandTheme(theme);
    return {
        ...DEFAULT_ITINERARY_PDF_THEME,
        fontFamily: normalized.fontFamily,
        colors: {
            orange: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.orange,
                400: normalized.colors.primary,
                500: normalized.colors.primary,
            },
            black: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.black,
                900: normalized.colors.text,
                950: normalized.colors.text,
            },
            blue: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.blue,
                500: normalized.colors.accent,
            },
            green: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.green,
                500: normalized.colors.success,
            },
            white: normalized.colors.background,
        },
    };
}
function isHexColor(value) {
    return typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value);
}
function normalizeNullableString(value) {
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}
function normalizeEmail(value) {
    const email = normalizeNullableString(value);
    return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}
function normalizeHttpsUrl(value) {
    const url = normalizeNullableString(value);
    return url && /^https:\/\//i.test(url) ? url : null;
}
function normalizePositiveInteger(value) {
    const numberValue = typeof value === "number"
        ? value
        : typeof value === "string"
            ? Number(value)
            : null;
    return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null;
}
